/*
 * Copyright (c) 2024. Bluemate B.V.
 */
import './style/existingUserSubscription.scss';

import {ChangeKeyFunction, CloseButton, Loader, preventDefault, WithBackground} from '@dvrd/dvr-controls';

import React, {MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import User, {UserFields} from '../../../models/userModel';

import {PersonalErrors} from '../../settings/common/userErrorsInterface';

import {ErrorResetter} from '../../../util/interfaces';

import {BluemateButton, BluemateInput} from '../../controls';

import CountryField from '../../controls/country/field/countryField';

type Errors = Pick<PersonalErrors, 'address' | 'zipCode' | 'city'>;

interface Props {
    onCancel: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChange: ChangeKeyFunction<keyof UserFields, any>;
    onResetError: ErrorResetter<Errors>;
    active: boolean;
    user: User;
    loading: boolean;
    errors: Errors;
    hasFullAddress: boolean;
}

export default function ExistingUserSubscription(props: Props) {
    const {user, active, loading, errors, onResetError, onCancel, onSubmit, onChange, hasFullAddress} = props;
    const {t} = useTranslation();

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <p className='message'>
                    {t(
                        'subscription.existingUser.messageLine1',
                        'Voor het toevoegen van een verkoopaccount heb je een eigen abonnement nodig.'
                    )}
                    <br />
                    {t(
                        'subscription.existingUser.messageLine2',
                        'Door verder te gaan starten we een gratis proefperiode van 30 dagen, deze loopt automatisch af.'
                    )}
                    <br />
                    {t(
                        'subscription.existingUser.messageLine3',
                        'Alleen als jij het wilt, wordt er daarna een abonnement gestart.'
                    )}
                    <br />
                    {t('subscription.existingUser.messageLine4', 'Je kunt de proefperiode te allen tijde stopzetten.')}
                </p>
                {!hasFullAddress && (
                    <form className='user-data-container' onSubmit={preventDefault}>
                        <p className='data-message'>
                            {t(
                                'subscription.existingUser.dataMessage',
                                'Voor het starten van de proefperiode hebben we de volgende gegevens nodig:'
                            )}
                        </p>
                        <BluemateInput
                            onChange={onChange('address')}
                            value={user.address}
                            label={t('subscription.existingUser.address', 'Adres')}
                            placeholder={t('subscription.existingUser.addressPlaceholder', 'Straatnaam + huisnummer')}
                            error={errors.address}
                            onFocus={onResetError('address')}
                        />
                        <BluemateInput
                            onChange={onChange('zip_code')}
                            value={user.zipCode}
                            label={t('subscription.existingUser.zipCode', 'Postcode')}
                            placeholder={t('subscription.existingUser.zipCode', 'Postcode')}
                            error={errors.zipCode}
                            onFocus={onResetError('zipCode')}
                        />
                        <CountryField
                            onChange={onChange('country')}
                            value={user.country}
                            itemsPosition='top'
                            itemsHeight='15rem'
                            labelAsValue
                        />
                        <BluemateInput
                            onChange={onChange('city')}
                            value={user.city}
                            label={t('subscription.existingUser.city', 'Plaats')}
                            placeholder={t('subscription.existingUser.city', 'Plaats')}
                            error={errors.city}
                            onFocus={onResetError('city')}
                        />
                    </form>
                )}
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onCancel} preventBackgroundClick escapeDisabled>
            <div className='default-popup existing-user-subscription'>
                <div className='header-container'>
                    <label className='header-title'>{t('subscription.existingUser.title', 'Abonnement vereist')}</label>
                    <CloseButton onClick={onCancel} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BluemateButton
                        onClick={onCancel}
                        label={t('subscription.existingUser.cancel', 'Annuleren')}
                        disabled={loading}
                        secondary
                    />
                    <BluemateButton
                        onClick={onSubmit}
                        label={t('subscription.existingUser.startTrial', 'Proefperiode starten')}
                        disabled={loading}
                    />
                </div>
            </div>
        </WithBackground>
    );
}
